/**
 * Batch A13 (2026-03-02) — First-touch UTM + attribution session snapshot.
 *
 * Captures utm_*, fbclid, gclid, landing_url, and document.referrer into
 * sessionStorage on the FIRST render of the session, then never overwrites.
 * The ad-driven candidate walk from `/jobs/:slug` all the way to a booked
 * slot may cross many routes, most of which will NOT carry UTMs — this
 * module ensures the ad's UTMs are still available at booking time.
 *
 * Also provides `bmiCapture(event, extra)` — a thin wrapper on
 * `posthog.capture` that automatically merges the stored first-touch
 * attribution into every event's properties.
 *
 * Session-scoped by design: a candidate who re-lands a week later (new
 * tab) will re-capture from the new URL. That's the correct behaviour
 * — the backend attribution field on the application document is the
 * PERSISTENT first-touch record; this module handles the intra-session
 * carry.
 */

const KEY = "bmi_first_touch_v1";

const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const CLICK_IDS = ["fbclid", "gclid"];

function _readSession() {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.sessionStorage.getItem(KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function _writeSession(rec) {
    if (typeof window === "undefined") return;
    try {
        window.sessionStorage.setItem(KEY, JSON.stringify(rec));
    } catch { /* private mode */ }
}

export function initUtmSession() {
    if (typeof window === "undefined") return null;
    // FIRST-TOUCH: never overwrite once set.
    const existing = _readSession();
    if (existing) return existing;

    let params;
    try {
        params = new URL(window.location.href).searchParams;
    } catch {
        params = new URLSearchParams();
    }

    const rec = {};
    for (const f of UTM_FIELDS) {
        const v = params.get(f);
        if (v && v.length <= 200) rec[f] = v.slice(0, 200);
    }
    for (const f of CLICK_IDS) {
        const v = params.get(f);
        if (v && v.length <= 500) rec[f] = v.slice(0, 500);
    }
    rec.landing_url = (window.location.href || "").slice(0, 1024);
    rec.referrer = (document.referrer || "").slice(0, 1024);
    rec.captured_at = new Date().toISOString();

    _writeSession(rec);
    return rec;
}

/** Read the first-touch snapshot as a flat props object. Never returns null. */
export function getFirstTouchUtms() {
    const rec = _readSession() || {};
    const out = {};
    for (const f of [...UTM_FIELDS, ...CLICK_IDS, "landing_url", "referrer"]) {
        if (rec[f]) out[f] = rec[f];
    }
    return out;
}

/**
 * Thin `posthog.capture` wrapper that always includes first-touch UTMs
 * plus the caller's `extra` properties. No-op if posthog is not loaded
 * (blocked / not initialised) so a broken analytics stack never breaks
 * the app flow.
 */
export function bmiCapture(event, extra = {}) {
    try {
        if (typeof window === "undefined" || !window.posthog) return;
        const props = {
            ...getFirstTouchUtms(),
            ...extra,
        };
        window.posthog.capture(event, props);
    } catch { /* silent — analytics must never break state changes */ }
}

/**
 * Idempotency helper: fires the event exactly once per (event, key)
 * per session, using sessionStorage. Use for state-change events
 * that could be re-triggered by a reload (bmi_apply_start,
 * bmi_register_complete, bmi_mcq_submitted).
 */
export function bmiCaptureOnce(event, key, extra = {}) {
    if (typeof window === "undefined") return;
    const guard = `bmi_evt_${event}_${key}`;
    try {
        if (window.sessionStorage.getItem(guard) === "1") return;
        window.sessionStorage.setItem(guard, "1");
    } catch { /* keep firing if private mode; better than silent zero */ }
    bmiCapture(event, extra);
}
